"use client"

import * as React from "react"
import { Button } from "@/components/ui/button"
import { NotificationItem } from "@/types/notification"
import { toast } from "sonner"
import { Bell, Trash2, Sparkles, CheckCircle, AlertCircle, AlertTriangle, Info } from "lucide-react"

interface MobileNotificationsProps {
  isOpen: boolean
  onClose: () => void
  notifications: NotificationItem[]
  setNotifications: React.Dispatch<React.SetStateAction<NotificationItem[]>>
  theme?: string
}

export function MobileNotifications({
  isOpen,
  onClose,
  notifications,
  setNotifications,
  theme,
}: MobileNotificationsProps) {
  if (!isOpen) return null

  const isLight = theme === "light"

  const getNotifIcon = (type: string) => {
    switch (type) {
      case "success": return <CheckCircle className="size-4 text-emerald-500 shrink-0" />
      case "error": return <AlertCircle className="size-4 text-red-500 shrink-0" />
      case "warning": return <AlertTriangle className="size-4 text-amber-500 shrink-0" />
      case "info": return <Info className="size-4 text-blue-500 shrink-0" />
      default: return <Bell className="size-4 text-blue-500 shrink-0" />
    }
  }

  return (
    <div className={`fixed inset-0 z-[999] flex flex-col backdrop-blur-xl animate-in slide-in-from-top duration-300 font-sans select-none ${
      isLight ? "bg-zinc-100/95 text-zinc-900 border-zinc-200" : "bg-zinc-950/90 text-white border-zinc-800"
    }`}>
      <div className="absolute inset-0 z-0" onClick={onClose} />

      <div className="relative z-10 flex flex-col h-full p-5 pb-20">
        <div className="flex flex-col items-center gap-1.5 pb-4 cursor-pointer" onClick={onClose}>
          <div className={`w-12 h-1 rounded-full ${isLight ? "bg-zinc-300" : "bg-white/30"}`} />
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div
              className={`size-9 rounded-2xl flex items-center justify-center ${
                notifications.length > 0 ? "bg-blue-500/20" : isLight ? "bg-zinc-200" : "bg-white/10"
              }`}
            >
              <Bell className="size-4 text-blue-500 shrink-0" />
            </div>
            <div>
              <h2 className={`text-base font-extrabold leading-none ${isLight ? "text-zinc-900" : "text-white"}`}>Notificações</h2>
              <p className={`text-[10px] mt-0.5 ${isLight ? "text-zinc-500" : "text-white/50"}`}>
                {notifications.length > 0
                  ? `${notifications.length} mensagem${notifications.length > 1 ? "s" : ""}`
                  : "Tudo em dia"}
              </p>
            </div>
          </div>

          {notifications.length > 0 && (
            <button
              onClick={() => {
                setNotifications([])
                toast.success("Histórico limpo com sucesso.")
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-500/15 border border-red-500/30 text-red-500 text-[10px] font-bold hover:bg-red-500/25 active:scale-95 transition-all cursor-pointer shadow-xs"
            >
              <Trash2 className="size-3 text-red-500 shrink-0" />
              <span>Limpar Tudo</span>
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto space-y-2.5 pr-1 scrollbar-none">
          {notifications.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 opacity-60">
              <Sparkles className="size-8 text-blue-500 shrink-0" />
              <div className="text-center">
                <p className={`text-sm font-bold ${isLight ? "text-zinc-800" : "text-white"}`}>Tudo limpo!</p>
                <p className={`text-[10px] mt-0.5 ${isLight ? "text-zinc-500" : "text-white/60"}`}>Nenhuma notificação pendente.</p>
              </div>
            </div>
          ) : (
            notifications.map((notif) => (
              <div
                key={notif.id}
                className={`p-3.5 rounded-2xl border space-y-1.5 transition-colors shadow-xs ${
                  isLight 
                    ? "bg-white border-zinc-200/80 hover:bg-zinc-50 text-zinc-900" 
                    : "bg-white/8 hover:bg-white/12 border-white/8 text-white"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {getNotifIcon(notif.type)}
                    <h5 className={`text-xs font-bold leading-snug truncate ${isLight ? "text-zinc-900" : "text-white"}`}>{notif.title}</h5>
                  </div>
                  <span className={`text-[9px] font-mono shrink-0 ${isLight ? "text-zinc-400" : "text-white/40"}`}>{notif.time}</span>
                </div>
                <p className={`text-[10px] leading-relaxed pl-6 ${isLight ? "text-zinc-600" : "text-white/65"}`}>{notif.text}</p>
                <div className="pl-6">
                  <span
                    className={`inline-block text-[8px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                      notif.type === "success"
                        ? "bg-green-500/20 text-green-500"
                        : notif.type === "error"
                        ? "bg-red-500/20 text-red-500"
                        : notif.type === "warning"
                        ? "bg-amber-500/20 text-amber-500"
                        : "bg-blue-500/20 text-blue-500"
                    }`}
                  >
                    {notif.type}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        <Button
          onClick={onClose}
          className={`mt-4 w-full rounded-2xl py-3 border font-bold text-sm cursor-pointer shadow-lg ${
            isLight ? "bg-zinc-200 hover:bg-zinc-300 text-zinc-900 border-zinc-300" : "bg-white/10 hover:bg-white/20 text-white border-white/10"
          }`}
        >
          Fechar
        </Button>
      </div>
    </div>
  )
}
